import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MessageSquare, Send, MoreVertical, Trash2, ShieldAlert, Heart, CornerDownRight, X, Loader2, User } from 'lucide-react';
import { CommentWithUser, Language } from '../../types';
import { calculateTimeAgo } from '../../services/formatService';
import { t } from '../../i18n';
import { translateText, detectLanguage } from '../../services/geminiService';
import { Globe } from 'lucide-react';
import ImageZoomModal from '../Common/ImageZoomModal';

interface NewsCommentSectionProps {
    comments: (CommentWithUser & { is_liked?: boolean })[];
    lang: Language;
    user: any;
    isPosting: boolean;
    newComment: string;
    onCommentChange: (val: string) => void;
    onPost: (parentId?: number | null) => void;
    onDelete: (id: string | number) => void;
    onLikeToggle: (id: string | number, isLiked: boolean) => void;
    onReport: (id: string | number) => void;
    onLoginPrompt: () => void;
}

export const NewsCommentSection: React.FC<NewsCommentSectionProps> = ({ 
    comments, lang, user, isPosting, newComment, onCommentChange, onPost, onDelete, onLikeToggle, onReport, onLoginPrompt 
}) => {
    const [activeMenuId, setActiveMenuId] = useState<string | number | null>(null);
    const [replyingTo, setReplyingTo] = useState<CommentWithUser | null>(null);
    const [zoomedImage, setZoomedImage] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // 외부 클릭 시 메뉴 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setActiveMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 댓글 구조화 (평면 리스트 -> 트리 구조)
    const structuredComments = useMemo(() => {
        const roots = comments.filter(c => !c.parent_id);
        const children = comments.filter(c => c.parent_id);
        
        return roots.map(root => ({
            ...root,
            replies: children.filter(c => String(c.parent_id) === String(root.id))
        })).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }, [comments]);

    const handleReplyClick = (comment: CommentWithUser) => {
        if (!user) { onLoginPrompt(); return; }
        // 대댓글의 대댓글은 방지하기 위해 항상 루트 댓글을 부모로 설정하거나 그대로 설정
        setReplyingTo(comment);
        // 포커스 이동 등을 위해 스크롤 처리 로직은 부모에서 처리 가능
    };

    const handleCancelReply = () => {
        setReplyingTo(null);
    };

    const handlePostInternal = () => {
        onPost(replyingTo ? Number(replyingTo.id) : null);
        setReplyingTo(null);
    };

    const CommentItem = ({ comment, isReply }: { comment: CommentWithUser & { is_liked?: boolean }, isReply: boolean }) => {
        const isLiked = !!comment.is_liked;
        const isMine = user && user.id === comment.user_id;
        const [isTranslating, setIsTranslating] = useState(false);
        const [translatedText, setTranslatedText] = useState<string | null>(null);
        const [showOriginal, setShowOriginal] = useState(false);

        const detectedLang = useMemo(() => detectLanguage(comment.content), [comment.content]);
        const isSameLanguage = detectedLang === lang || detectedLang === 'emoji';

        const handleTranslate = async () => {
            if (translatedText) {
                setShowOriginal(!showOriginal);
                return;
            }

            setIsTranslating(true);
            try {
                const result = await translateText(comment.content, lang);
                if (result) {
                    setTranslatedText(result);
                    setShowOriginal(false);
                }
            } catch (error) {
                console.error('Translation error:', error);
            } finally {
                setIsTranslating(false);
            }
        };

        return (
            <div key={comment.id} className={`group flex gap-3 md:gap-4 p-4 -mx-4 transition-all ${isReply ? 'ml-6 md:ml-10 bg-slate-50/30 rounded-2xl mt-1' : 'border-b border-gray-50 last:border-none'}`}>
                {isReply && (
                    <div className="pt-2 text-slate-300">
                        <CornerDownRight size={16} />
                    </div>
                )}
                <div className="shrink-0">
                    <div className="relative">
                        <div className={`rounded-full overflow-hidden bg-slate-100 border border-gray-100 shadow-sm ${isReply ? 'w-8 h-8' : 'w-10 h-10 md:w-12 md:h-12'}`}>
                            <img 
                                src={comment.user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (!target.src.includes('ui-avatars.com')) {
                                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user?.name || 'U')}&background=random`;
                                    }
                                }}
                                alt="" 
                                loading="lazy"
                            />
                        </div>
                        {comment.user?.favorite_team && (
                            <div className={`absolute -bottom-1 -right-1 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center z-20 overflow-hidden ${isReply ? 'w-4 h-4 p-0.5' : 'w-5 h-5 md:w-6 md:h-6 p-0.5'}`}>
                                <img src={comment.user.favorite_team.logo} alt={comment.user.favorite_team.name} className="w-full h-full object-contain" />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-black text-slate-900 text-sm truncate uppercase tracking-tight">
                                {comment.user?.name || 'Unknown Scout'}
                            </span>
                            <span className="text-slate-400 text-xs font-medium shrink-0">·</span>
                            <span className="text-slate-400 text-[10px] font-medium shrink-0 uppercase">
                                {calculateTimeAgo(comment.created_at)}
                            </span>
                        </div>

                        {/* 액션 메뉴 드롭다운 */}
                        <div className="relative" ref={activeMenuId === comment.id ? menuRef : null}>
                            <button 
                                onClick={(e) => { e.stopPropagation(); setActiveMenuId(activeMenuId === comment.id ? null : comment.id); }}
                                className="p-1 text-slate-300 md:hover:text-slate-900 transition-all rounded-full md:hover:bg-gray-100"
                            >
                                <MoreVertical size={16} />
                            </button>
                            {activeMenuId === comment.id && (
                                <div className="absolute top-full right-0 mt-1 w-32 bg-white border border-gray-100 rounded-xl shadow-xl z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
                                    <div className="p-1 flex flex-col">
                                        <button 
                                            onClick={() => { setActiveMenuId(null); onReport(comment.id); }}
                                            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-tight text-slate-600 md:hover:bg-slate-50 transition-colors"
                                        >
                                            <ShieldAlert size={14} className="text-rose-500" />
                                            <span>{t(lang, 'report')}</span>
                                        </button>
                                        {isMine && (
                                            <button 
                                                onClick={() => { setActiveMenuId(null); onDelete(comment.id); }}
                                                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-tight text-rose-500 md:hover:bg-rose-50 transition-colors"
                                            >
                                                <Trash2 size={14} />
                                                <span>{t(lang, 'delete')}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <p className="text-slate-700 text-[15px] leading-relaxed font-medium mb-1">
                        {showOriginal ? comment.content : (translatedText || comment.content)}
                    </p>

                    {comment.image_url && (
                        <div 
                            className="mt-2 mb-3 rounded-2xl overflow-hidden border border-gray-100 bg-slate-50 max-w-64 cursor-zoom-in"
                            onClick={() => setZoomedImage(comment.image_url!)}
                        >
                            <img 
                                src={comment.image_url} 
                                alt="" 
                                className="w-full h-auto object-cover max-h-64 hover:opacity-95 transition-opacity" 
                                loading="lazy"
                            />
                        </div>
                    )}

                    {/* Translation Button */}
                    {comment.content && !isSameLanguage && (
                        <button
                            onClick={handleTranslate}
                            disabled={isTranslating}
                            className="mb-3 flex items-center gap-1.5 text-[11px] font-bold text-indigo-500 hover:text-indigo-600 transition-colors"
                        >
                            {isTranslating ? (
                                <Loader2 size={12} className="animate-spin" />
                            ) : (
                                <Globe size={12} />
                            )}
                            {showOriginal ? t(lang, 'viewTranslation') : (translatedText ? t(lang, 'viewOriginal') : t(lang, 'viewTranslation'))}
                        </button>
                    )}

                    <div className="flex items-center gap-6 text-slate-400">
                        <button 
                            onClick={() => onLikeToggle(comment.id, isLiked)}
                            className={`flex items-center gap-1.5 transition-all active:scale-90 ${isLiked ? 'text-rose-500' : 'md:hover:text-rose-500'}`}
                        >
                            <Heart size={15} fill={isLiked ? 'currentColor' : 'transparent'} />
                            <span className="text-[10px] font-black tabular-nums">{comment.likes_count || 0}</span>
                        </button>
                        {!isReply && (
                            <button 
                                onClick={() => handleReplyClick(comment)}
                                className="text-[10px] font-black uppercase tracking-widest md:hover:text-slate-900 transition-colors"
                            >
                                {t(lang, 'reply')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="pb-20">
            <div className="flex items-center justify-between mb-8 px-1">
                <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">{t(lang, 'comments')}</h2>
                <span className="hidden bg-slate-100 text-slate-500 text-[10px] font-black px-2 py-0.5 rounded-full">{comments.length}</span>
            </div>
            
            {/* 답글 대상 표시 (대댓글 작성 모드일 때) */}
            {replyingTo && (
                <div className="mb-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-between animate-in slide-in-from-bottom-2 duration-300">
                    <div className="flex items-center gap-2">
                        <CornerDownRight size={14} className="text-indigo-500" />
                        <span className="text-[11px] font-bold text-indigo-600">
                            {t(lang, 'replyingTo')} <span className="font-black">@{replyingTo.user?.name}</span>
                        </span>
                    </div>
                    <button onClick={handleCancelReply} className="text-indigo-400 md:hover:text-rose-500"><X size={14} strokeWidth={3}/></button>
                </div>
            )}

            {/* 입력 폼 */}
            <div className={`flex gap-4 mb-10 pb-6 border-b border-gray-100 transition-all ${replyingTo ? 'pt-2' : ''}`}>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-gray-100">
                    {user ? <img src={user.avatar} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><User size={20} /></div>}
                </div>
                <div className="flex-1">
                    <textarea 
                        className="w-full bg-transparent border-none focus:ring-0 text-base font-medium text-slate-800 placeholder:text-slate-400 resize-none outline-none py-1 h-auto min-h-[40px] max-h-32"
                        placeholder={user ? (replyingTo ? t(lang, 'typeIntelligenceUpdate') : t(lang, 'joinConversation')) : t(lang, 'loginParticipate')}
                        value={newComment}
                        onChange={(e) => onCommentChange(e.target.value)}
                        disabled={isPosting}
                        onClick={() => !user && onLoginPrompt()}
                        rows={1}
                    />
                    <div className="flex justify-end mt-2">
                        <button 
                            onClick={handlePostInternal}
                            disabled={!newComment.trim() || isPosting}
                            className="bg-slate-900 text-white px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest md:hover:bg-rose-500 transition-all disabled:opacity-20 shadow-lg shadow-slate-100"
                        >
                            {isPosting ? <Loader2 size={14} className="animate-spin" /> : (replyingTo ? t(lang, 'reply') : t(lang, 'post'))}
                        </button>
                    </div>
                </div>
            </div>

            {/* 댓글 리스트 (구조화된 트리형태 렌더링) */}
            {comments.length > 0 ? (
                <div className="flex flex-col space-y-2">
                    {structuredComments.map((root) => (
                        <div key={root.id} className="relative">
                            <CommentItem comment={root} isReply={false} />
                            {root.replies.length > 0 && (
                                <div className="space-y-1">
                                    {root.replies.map(reply => (
                                        <CommentItem key={reply.id} comment={reply} isReply={true} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center flex flex-col items-center justify-center text-slate-300">
                    <MessageSquare size={48} className="opacity-10 mb-4" />
                    <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">{t(lang, 'firstIntelligenceReport')}</p>
                </div>
            )}

            <ImageZoomModal 
                imageUrl={zoomedImage || ''}
                isOpen={!!zoomedImage}
                onClose={() => setZoomedImage(null)}
            />
        </section>
    );
};
